/**
 * @fileoverview Canonical hook for revenue vs costs time series with full period fields.
 * @module shared/hooks/dashboard/useRevenueVsCostsSeries
 */

import { useMemo } from 'react'
import { useDashboardData } from './useDashboardData'

/**
 * Revenue vs costs series data point with all period fields.
 */
export interface RevenueVsCostsSeriesPoint {
  /** Period name */
  period: string
  /** Revenue (sales total) */
  revenue: number
  /** Cost of goods sold */
  cogs: number
  /** Advertising spend total */
  adSpend: number
  /** Net profit (fallback to revenue - (adSpend + cogs) if missing) */
  profit: number
  /** Total expenses (fallback to adSpend + cogs if missing) */
  totalExpenses: number
}

/**
 * Hook result for revenue vs costs series.
 */
export interface UseRevenueVsCostsSeriesResult {
  /** Time series data with full period fields */
  data: RevenueVsCostsSeriesPoint[]
  /** Loading state */
  isLoading: boolean
  /** Error state */
  isError: boolean
  /** Error message */
  error: string | null
}

/**
 * Canonical hook to get revenue vs costs series data with all period fields.
 *
 * This selector provides comprehensive time-series data including revenue, COGS,
 * ad spend, profit, and total expenses for each period. It serves as the single
 * source of truth for time-series visualizations that need access to all cost
 * components.
 *
 * @returns Revenue vs costs series data with loading and error states
 * @example
 * ```typescript
 * const { data, isLoading } = useRevenueVsCostsSeries();
 *
 * // Access all period fields
 * data.forEach(point => {
 *   console.log({
 *     period: point.period,
 *     revenue: point.revenue,
 *     cogs: point.cogs,
 *     adSpend: point.adSpend,
 *     profit: point.profit,
 *     totalExpenses: point.totalExpenses
 *   });
 * });
 * ```
 */
export function useRevenueVsCostsSeries(): UseRevenueVsCostsSeriesResult {
  const { salesTimeQuery } = useDashboardData()

  const data = useMemo(() => {
    const dataPoints = salesTimeQuery.data?.data || []

    // Helper to parse various period formats (YYYY-MM-DD, YYYY-MM, "Jan 2024")
    const toTimestamp = (period: string | undefined): number => {
      const p = period?.trim() || ''
      // YYYY-MM-DD
      const m1 = /^(\d{4})-(\d{2})-(\d{2})$/.exec(p)
      if (m1) {
        const [_, y, m, d] = m1
        return Date.UTC(Number(y), Number(m) - 1, Number(d))
      }
      // YYYY-MM
      const m2 = /^(\d{4})-(\d{2})$/.exec(p)
      if (m2) {
        const [_, y, m] = m2
        return Date.UTC(Number(y), Number(m) - 1, 1)
      }
      // Try native parser for strings like "Jan 2024"
      const t = Date.parse(p)
      if (!Number.isNaN(t)) return t
      // Fallback to 0 to keep stable order later
      return 0
    }

    // Sort data points chronologically by parsed timestamp
    const sortedDataPoints = [...dataPoints].sort((a, b) => {
      const ta = toTimestamp(
        typeof a.dimensions.timePeriod === 'string' ? a.dimensions.timePeriod : undefined
      )
      const tb = toTimestamp(
        typeof b.dimensions.timePeriod === 'string' ? b.dimensions.timePeriod : undefined
      )
      if (ta !== tb) return ta - tb
      // Fallback deterministic order by string when equal/unknown
      const sa = typeof a.dimensions.timePeriod === 'string' ? a.dimensions.timePeriod : ''
      const sb = typeof b.dimensions.timePeriod === 'string' ? b.dimensions.timePeriod : ''
      return sa.localeCompare(sb)
    })

    return sortedDataPoints.map((point): RevenueVsCostsSeriesPoint => {
      const period =
        typeof point.dimensions.timePeriod === 'string' ? point.dimensions.timePeriod : ''
      const revenue = point.metrics.sales ?? 0
      const cogs = point.metrics.cogs ?? 0
      const adSpend = point.metrics.advertising ?? 0
      const directCosts = point.metrics.directCosts ?? 0
      const totalExpenses = adSpend + cogs + directCosts
      const profit =
        typeof point.metrics.cm2 === 'number' ? point.metrics.cm2 : revenue - totalExpenses

      return {
        period,
        revenue,
        cogs,
        adSpend,
        profit,
        totalExpenses,
      }
    })
  }, [salesTimeQuery.data])

  return {
    data,
    isLoading: salesTimeQuery.isLoading,
    isError: salesTimeQuery.isError,
    error: salesTimeQuery.error?.message || null,
  }
}
